import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { CompanySettings } from '@domain/account/models/Company';
import { ProductStatus } from '@domain/product/models/ProductFilter';
import { useTutorialStore } from '../../../../application/store/TutorialStore';
import { useCompanySettings } from '@features/company/hooks/useCompanySettings';
import { useConnections } from '@features/connections/generic/hooks/useConnections';
import { useProducts } from '@features/products/hooks/useProducts';
import { useToast } from '@shared/hooks/useToast';
import OnboardingOverlayCard from '../../shared/OnboardingOverlayCard';
import OnboardingSkipFooter from '../../shared/OnboardingSkipFooter';
import ProductCatalogEmptyView from './ProductCatalogEmptyView';
import ProductCatalogFinishedStep from './ProductCatalogFinishedStep';
import ProductCatalogSetupView from './ProductCatalogSetupView';
import {
  ACTIVE_JOBS_POLL_MS,
  areDefaultsComplete,
  CatalogSubstep,
  nextIncompleteSubstep,
} from './defaults';

interface ProductCatalogOnboardingStepProps {
  onDismiss: () => void;
}

const ProductCatalogOnboardingStep: React.FC<ProductCatalogOnboardingStepProps> = ({
  onDismiss,
}) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const { markStepCompleted, setCurrentStep } = useTutorialStore();
  const { settings, isLoading: isLoadingSettings, updateSettings } = useCompanySettings();
  const { connections } = useConnections();
  const {
    products: productsPage,
    isLoading: isLoadingProducts,
    bulkEditProducts,
  } = useProducts({ active: true, limit: 100 }, ProductStatus.ACTIVE);

  const { data: activeJobs } = useQuery({
    queryKey: ['active-import-jobs'],
    queryFn: async () => {
      const response = await container.importJobsRepository.fetchActiveJobs();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    refetchInterval: ACTIVE_JOBS_POLL_MS,
  });

  const isImportingProducts = useMemo(
    () => (activeJobs ?? []).some((job) => job.target === 'products'),
    [activeJobs],
  );
  const wasImportingRef = useRef(false);

  useEffect(() => {
    if (wasImportingRef.current && !isImportingProducts) {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
    wasImportingRef.current = isImportingProducts;
  }, [isImportingProducts, queryClient]);

  const productCount = productsPage?.status === 'success' ? productsPage.total : 0;
  const hasConnection = (connections?.length ?? 0) > 0;
  const defaultsComplete = areDefaultsComplete(settings);

  const [activeSubstep, setActiveSubstep] = useState<CatalogSubstep | null>(null);
  const [submitting, setSubmitting] = useState<CatalogSubstep | null>(null);

  useEffect(() => {
    if (isLoadingSettings || isLoadingProducts || isImportingProducts) return;
    if (defaultsComplete) {
      markStepCompleted('products');
      setCurrentStep(2);
      return;
    }
  }, [
    defaultsComplete,
    isImportingProducts,
    isLoadingProducts,
    isLoadingSettings,
    markStepCompleted,
    setCurrentStep,
    settings,
  ]);

  useEffect(() => {
    if (isImportingProducts) setActiveSubstep(null);
  }, [isImportingProducts]);

  const saveDefaults = async (
    substep: CatalogSubstep,
    settingsPatch: Partial<CompanySettings>,
    productUpdate: Record<string, unknown>,
  ) => {
    setSubmitting(substep);
    try {
      const updated = await updateSettings(settingsPatch);
      if (productCount > 0) {
        await bulkEditProducts({ updateData: productUpdate, filters: { active: true } });
      }
      success('Defaults saved');
      const next = nextIncompleteSubstep(updated);
      setActiveSubstep(next);
      if (!next) {
        markStepCompleted('products');
        setCurrentStep(2);
      }
    } catch {
      showError('Failed to save defaults');
    } finally {
      setSubmitting(null);
    }
  };

  if (isLoadingSettings || isLoadingProducts) {
    return (
      <OnboardingOverlayCard
        title="Set up your product catalog"
        footer={<OnboardingSkipFooter onDismiss={onDismiss} />}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      </OnboardingOverlayCard>
    );
  }

  if (defaultsComplete) {
    return <ProductCatalogFinishedStep onDismiss={onDismiss} />;
  }

  if (!hasConnection && productCount === 0) {
    return <ProductCatalogEmptyView onDismiss={onDismiss} />;
  }

  return (
    <ProductCatalogSetupView
      onCancel={() => setActiveSubstep(null)}
      settings={settings}
      activeSubstep={activeSubstep}
      submitting={submitting}
      isImportingProducts={isImportingProducts}
      onDismiss={onDismiss}
      onActivate={(id) => {
        if (!isImportingProducts) setActiveSubstep(id);
      }}
      onSave={saveDefaults}
    />
  );
};

export default ProductCatalogOnboardingStep;
